import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, updateData, deleteData, activateData, postData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Box,
  Snackbar,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  useMediaQuery,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  FilterList,
  Refresh,
  Assignment,
  Edit as EditIcon,
  Warning,
  CheckCircle,
  Add
} from '@mui/icons-material';
import PurchaseReturnModal from './PurchaseReturnModal';
import { printPurchaseReturnPDF } from './PurchaseReturnPdfGenerator';
import '../Purchase/PurchaseIndex.css';

const PurchaseReturnAgainstIndex = () => {
  // Session validation
  useSessionValidation();

  const { token, companyId, branchId, userId } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Mobile detection
  const isMobile = useMediaQuery('(max-width:600px)');

  // State management
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnDetails, setReturnDetails] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [vatType, setVATType] = useState(1); // 1 = Excluding Tax, 2 = Including Tax
  const [editReturnDetails, setEditReturnDetails] = useState([]);

  // Cancel confirmation modal for Edit
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [originalEditDetails, setOriginalEditDetails] = useState([]);

  // Delete confirmation modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ billNumber: '', supplier: '' });

  // Activate confirmation modal
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemInfo, setActivateItemInfo] = useState({ billNumber: '', supplier: '' });

  // Create Purchase Return modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Currency dropdown state for edit modal
  const [editCurrencies, setEditCurrencies] = useState([]);
  const [editCurrencyLoading, setEditCurrencyLoading] = useState(false);

  // Print state
  const [printingItemId, setPrintingItemId] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch Suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      const supplierData = response.response_data || response || [];
      setSuppliers(Array.isArray(supplierData) ? supplierData : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  // Fetch Purchase Returns (Active and Inactive)
  const fetchPurchaseReturns = async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchase returns...');
    try {
      // Fetch Active Purchase Returns
      const activeResponse = await fetchData('PurchaseReturnApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({
        ...item,
        IsActive: item.IsActive ?? true
      }));

      // Fetch Inactive Purchase Returns
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseReturnApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({
          ...item,
          IsActive: false
        }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchase returns:', inactiveError);
      }

      setPurchaseReturns([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching purchase returns:', error);
      setError('Failed to fetch purchase returns');
      setPurchaseReturns([]);
    }
    setLoading(false);
  };

  // Fetch inactive Purchase Return details for view modal
  const fetchInactivePurchaseReturnDetails = async (id) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/InActivePurchaseReturnListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase return details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchSuppliers();
      fetchPurchaseReturns();
    }
  }, [companyId, token, fromDate, toDate]);

  // Track changes in edit form
  useEffect(() => {
    if (editModalOpen && originalEditData) {
      const formChanged = JSON.stringify(editFormData) !== JSON.stringify(originalEditData);
      const detailsChanged = JSON.stringify(editReturnDetails) !== JSON.stringify(originalEditDetails);
      setHasUnsavedChanges(formChanged || detailsChanged);
    }
  }, [editFormData, editReturnDetails, originalEditData, originalEditDetails, editModalOpen]);

  // Fetch currencies when edit modal opens
  useEffect(() => {
    const fetchCurrencies = async () => {
      if (!companyId || !token || !editModalOpen) return;
      setEditCurrencyLoading(true);
      try {
        const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
        if (Array.isArray(response)) {
          setEditCurrencies(response);
        } else if (response?.response_data && Array.isArray(response.response_data)) {
          setEditCurrencies(response.response_data);
        } else {
          setEditCurrencies([]);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
        setEditCurrencies([]);
      } finally {
        setEditCurrencyLoading(false);
      }
    };

    fetchCurrencies();
  }, [companyId, token, editModalOpen]);

  // Handle View
  const handleView = async (purchaseReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      let response;
      // Use different API endpoint for inactive items
      if (purchaseReturn.IsActive === false) {
        response = await fetchInactivePurchaseReturnDetails(purchaseReturn.Id);
      } else {
        response = await fetchData('PurchaseReturnApi', `/${purchaseReturn.Id}`, token);
        response = response.response_data || response;
      }

      setSelectedReturn(response.PurchaseReturn || response);
      setReturnDetails(response.PurchaseReturnDetails || []);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      setError('Failed to fetch purchase return details');
    }
    setLoading(false);
  };

  // Handle Edit
  const handleEdit = async (purchaseReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      // const response = await fetchData('PurchaseReturnApi', `/PurchaseReturn/${purchaseReturn.Id}/${branchId}`, token);
      const response = await fetchData('PurchaseReturnApi', `/${purchaseReturn.Id}`, token);
      const data = response.response_data || response;
      const returnData = data.PurchaseReturn || data;
      const details = data.PurchaseReturnDetails || [];

      setSelectedReturn(returnData);
      setReturnDetails(details);

      // Initialize edit return details with return quantities
      const initialEditDetails = details.map(detail => ({
        ...detail,
        ReturnQuantity: detail.Quantity || 0,
        // Use PurchaseQuantity from API response
        PurchasedQuantity: detail.PurchaseQuantity || detail.Quantity || 0,
        // Store original values for calculations
        OriginalRate: detail.Rate || 0,
        OriginalVATRate: detail.VATRate || 0
      }));
      setEditReturnDetails(initialEditDetails);

      setVATType(returnData.VATCalculationType || 1);

      const formData = {
        BillNumber: returnData.BillNumber || '',
        BillDate: returnData.BillDate?.split('T')[0] || '',
        PurchaseNo: returnData.PurchaseNo || '',
        MasterName: returnData.MasterName || '',
        Remarks: returnData.Remarks || '',
        InvoiceTypeName: returnData.InvoiceTypeName || '',
        VATCalculationType: returnData.VATCalculationType || 1,
        CurrencyTypeId: returnData.CurrencyTypeId || null,
        CurrencyName: returnData.CurrencyName || '',
        Amount: returnData.Amount || 0,
        DiscountAmount: returnData.DiscountAmount || 0,
        TaxableAmount: returnData.TaxableAmount || 0,
        // CGSTAmount: returnData.CGSTAmount || 0,
        // SGSTAmount: returnData.SGSTAmount || 0,
        VATAmount: returnData.VATAmount || 0,
        IGSTAmount: returnData.IGSTAmount || 0,
        TotalAmount: returnData.TotalAmount || 0,
        FreightAmount: returnData.FreightAmount || 0,
        FreightVATAmount: returnData.FreightVATAmount || 0,
        FreightVATRate: returnData.FreightVATRate || 5,
        PackingAmount: returnData.PackingAmount || 0,
        PackingVATAmount: returnData.PackingVATAmount || 0,
        PackingVATRate: returnData.PackingVATRate || 5,
        TCSAmount: returnData.TCSAmount || 0,
        PaidAmount: returnData.PaidAmount || 0,
        RoundOff: returnData.RoundOff || 0
      };

      setEditFormData(formData);

      // Store original data for change detection
      setOriginalEditData(JSON.parse(JSON.stringify(formData)));
      setOriginalEditDetails(JSON.parse(JSON.stringify(initialEditDetails)));
      setHasUnsavedChanges(false);

      // Calculate GST rates based on items after a short delay to ensure state is set
      setTimeout(() => {
        // Calculate the effective GST rates
        const maxItemVATRate = initialEditDetails.length > 0
          ? Math.max(...initialEditDetails.map(d => parseFloat(d.VATRate) || 0))
          : 0;
        const effectiveFreightVATRate = Math.max(5, maxItemVATRate);
        const effectivePackingVATRate = Math.max(5, maxItemVATRate);

        // Update freight and packing GST amounts with correct rates
        const freightAmount = parseFloat(formData.FreightAmount) || 0;
        const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;
        const packingAmount = parseFloat(formData.PackingAmount) || 0;
        const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;

        setEditFormData(prev => ({
          ...prev,
          FreightVATRate: effectiveFreightVATRate,
          FreightVATAmount: freightVATAmount,
          PackingVATRate: effectivePackingVATRate,
          PackingVATAmount: packingVATAmount
        }));
      }, 100);

      setEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      setError('Failed to fetch purchase return details');
    }
    setLoading(false);
  };

  // Check if invoice type is IGST
  // B to B and B to C use CGST+SGST, others use IGST
  const isIGSTInvoice = () => {
    const invoiceType = (editFormData.InvoiceTypeName || '').toLowerCase();
    // If invoice type contains 'b to b' or 'b to c' or 'b2b' or 'b2c', use CGST+SGST
    if (invoiceType.includes('b to b') || invoiceType.includes('b to c') ||
      invoiceType.includes('b2b') || invoiceType.includes('b2c')) {
      return false; // Use CGST + SGST
    }
    // For IGST type or any other type, use IGST
    return invoiceType.includes('igst') || invoiceType.length > 0;
  };

  // Check if tax is including
  const isIncludingTax = () => vatType === 2;

  // Handle return quantity change
  const handleReturnQuantityChange = (index, value) => {
    console.log('Return Qty Change:', { index, value, editReturnDetails: editReturnDetails[index] });

    // Allow empty string for user to clear the field
    if (value === '') {
      const updatedDetails = [...editReturnDetails];
      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: 0,
        Quantity: 0,
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        // CGSTAmount: 0,
        // SGSTAmount: 0,
        VATAmount: 0,
        IGSTAmount: 0,
        TotalAmount: 0
      };
      setEditReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    // Validate quantity
    if (numValue < 0) {
      setError('Return quantity cannot be negative');
      return;
    }
    if (numValue > detail.PurchasedQuantity) {
      setError(`Return quantity cannot exceed purchased quantity (${detail.PurchasedQuantity})`);
      return;
    }

    const returnQty = numValue;
    const rate = parseFloat(detail.Rate) || 0;
    const discountPercent = parseFloat(detail.DiscountPercentage) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      // Rate includes GST - calculate backwards
      const totalWithVat = returnQty * rate;
      baseAmount = totalWithVat / (1 + vatRate / 100);
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: Math.round(amount * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        // CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
        IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
      };
    } else {
      // Rate excludes GST - normal calculation
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: Math.round(amount * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        //CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
        IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
        TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
      };
    }

    console.log('Updated Detail:', updatedDetails[index]);
    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount percentage change
  const handleDiscountPercentChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      setError('Discount percentage must be between 0 and 100');
      return;
    }

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      const totalWithGst = returnQty * rate;
      baseAmount = totalWithGst / (1 + vatRate / 100);
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      vatAmount = (taxableAmount * vatRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: numValue,
      Amount: Math.round(amount * 100) / 100,
      DiscountAmount: Math.round(discountAmount * 100) / 100,
      TaxableAmount: Math.round(taxableAmount * 100) / 100,
      // CGSTAmount: useIGST ? 0 : gstAmount / 2,
      VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
      IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
      TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount amount change
  const handleDiscountAmountChange = (index, value) => {
    const numValue = parseFloat(value) || 0;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const vatRate = parseFloat(detail.VATRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, vatAmount, amount, discountPercent, baseAmount;

    if (isIncludingTax()) {
      const totalWithVat = returnQty * rate;
      baseAmount = totalWithVat / (1 + vatRate / 100);
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      vatAmount = (taxableAmount * vatRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      vatAmount = (taxableAmount * vatRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: Math.round(discountPercent * 100) / 100,
      DiscountAmount: Math.round(numValue * 100) / 100,
      Amount: Math.round(amount * 100) / 100,
      TaxableAmount: Math.round(taxableAmount * 100) / 100,
      //CGSTAmount: useIGST ? 0 : vatAmount / 2,
      VATAmount: useIGST ? 0 : Math.round(vatAmount * 100) / 100,
      IGSTAmount: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
      TotalAmount: Math.round((taxableAmount + vatAmount) * 100) / 100
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Calculate return totals
  const calculateReturnTotals = (details, additionalCharges = {}) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      //CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.VATAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Calculate freight and packing VAT rates
    // Use the maximum of default 5% OR the highest VAT rate among items
    const defaultFreightVATRate = 5;
    const defaultPackingVATRate = 5;
    const maxItemVATRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.VATRate) || 0)) : 0;

    const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
    const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

    // Use passed values or current state values
    const freightAmount = additionalCharges.FreightAmount !== undefined
      ? parseFloat(additionalCharges.FreightAmount) || 0
      : parseFloat(editFormData.FreightAmount) || 0;
    const freightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;

    const packingAmount = additionalCharges.PackingAmount !== undefined
      ? parseFloat(additionalCharges.PackingAmount) || 0
      : parseFloat(editFormData.PackingAmount) || 0;
    const packingVATAmount = (packingAmount * effectivePackingVATRate) / 100;

    const tcsAmount = additionalCharges.TCSAmount !== undefined
      ? parseFloat(additionalCharges.TCSAmount) || 0
      : parseFloat(editFormData.TCSAmount) || 0;

    const grandTotal = totals.TotalAmount + freightAmount + freightVATAmount + packingAmount + packingVATAmount + tcsAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const totalAmount = Math.round(grandTotal);

    setEditFormData(prev => ({
      ...prev,
      ...totals,
      FreightAmount: freightAmount,
      FreightVATRate: effectiveFreightVATRate,
      FreightVATAmount: freightVATAmount,
      PackingAmount: packingAmount,
      PackingVATRate: effectivePackingVATRate,
      PackingVATAmount: packingVATAmount,
      TCSAmount: tcsAmount,
      RoundOff: roundOff,
      TotalAmount: totalAmount
    }));
  };

  // Handle VAT Type change
  const handleVATTypeChange = (value) => {
    const intValue = parseInt(value);
    setVATType(intValue);
    setEditFormData(prev => ({ ...prev, VATCalculationType: intValue }));

    // Recalculate all items with new VAT type
    const updatedDetails = editReturnDetails.map(detail => {
      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const vatRate = parseFloat(detail.VATRate) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, vatAmount, amount;

      if (intValue === 2) {
        // Including Tax
        const totalWithVat = returnQty * rate;
        taxableAmount = totalWithVat / (1 + vatRate / 100);
        amount = taxableAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      } else {
        // Excluding Tax
        amount = returnQty * rate;
        taxableAmount = amount;
        vatAmount = (taxableAmount * vatRate) / 100;
      }

      return {
        ...detail,
        Amount: amount,
        TaxableAmount: taxableAmount,
        //CGSTAmount: useIGST ? 0 : gstAmount / 2,
        VATAmount: useIGST ? 0 : vatAmount,
        IGSTAmount: useIGST ? vatAmount : 0,
        TotalAmount: taxableAmount + vatAmount
      };
    });

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle additional charges change
  const handleAdditionalChargeChange = (field, value) => {
    const numValue = parseFloat(value) || 0;

    // Create updated charges object
    const updatedCharges = {
      FreightAmount: field === 'FreightAmount' ? numValue : editFormData.FreightAmount,
      PackingAmount: field === 'PackingAmount' ? numValue : editFormData.PackingAmount,
      TCSAmount: field === 'TCSAmount' ? numValue : editFormData.TCSAmount
    };

    // Recalculate with new values
    if (editReturnDetails.length > 0) {
      calculateReturnTotals(editReturnDetails, updatedCharges);
    } else {
      setEditFormData(prev => ({ ...prev, [field]: numValue }));
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    setLoading(true);
    setLoadingMessage('Saving changes...');
    try {
      // Prepare the update payload with PurchaseReturn and PurchaseReturnDetails
      const updatePayload = {
        PurchaseReturn: {
          ...selectedReturn,
          BillDate: editFormData.BillDate,
          Remarks: editFormData.Remarks,
          VATCalculationType: editFormData.VATCalculationType,
          Amount: Math.round(parseFloat(editFormData.Amount || 0) * 100) / 100,
          DiscountAmount: Math.round(parseFloat(editFormData.DiscountAmount || 0) * 100) / 100,
          TaxableAmount: Math.round(parseFloat(editFormData.TaxableAmount || 0) * 100) / 100,
          //CGSTAmount: editFormData.CGSTAmount,
          VATAmount: Math.round(parseFloat(editFormData.VATAmount || 0) * 100) / 100,
          IGSTAmount: Math.round(parseFloat(editFormData.IGSTAmount || 0) * 100) / 100,
          TotalAmount: Math.round(parseFloat(editFormData.TotalAmount || 0) * 100) / 100,
          FreightAmount: Math.round(parseFloat(editFormData.FreightAmount || 0) * 100) / 100,
          FreightVATAmount: Math.round(parseFloat(editFormData.FreightVATAmount || 0) * 100) / 100,
          FreightVATRate: editFormData.FreightVATRate,
          PackingAmount: Math.round(parseFloat(editFormData.PackingAmount || 0) * 100) / 100,
          PackingVATAmount: Math.round(parseFloat(editFormData.PackingVATAmount || 0) * 100) / 100,
          PackingVATRate: editFormData.PackingVATRate,
          TCSAmount: Math.round(parseFloat(editFormData.TCSAmount || 0) * 100) / 100,
          PaidAmount: Math.round(parseFloat(editFormData.PaidAmount || 0) * 100) / 100,
          RoundOff: Math.round(parseFloat(editFormData.RoundOff || 0) * 100) / 100,
          BalanceAmount: Math.round((parseFloat(editFormData.TotalAmount || 0) - parseFloat(editFormData.PaidAmount || 0)) * 100) / 100
        },
        PurchaseReturnDetails: editReturnDetails.map(detail => ({
          ...detail,
          Quantity: detail.ReturnQuantity || detail.Quantity,
          Amount: Math.round(parseFloat(detail.Amount || 0) * 100) / 100,
          TaxableAmount: Math.round(parseFloat(detail.TaxableAmount || 0) * 100) / 100,
          //CGSTAmount: detail.CGSTAmount,
          VATAmount: Math.round(parseFloat(detail.VATAmount || 0) * 100) / 100,
          IGSTAmount: Math.round(parseFloat(detail.IGSTAmount || 0) * 100) / 100,
          TotalAmount: Math.round(parseFloat(detail.TotalAmount || 0) * 100) / 100
        }))
      };

      await updateData('PurchaseReturnApi', `${selectedReturn.Id}`, updatePayload, token);
      setSuccess('Purchase return updated successfully');
      setHasUnsavedChanges(false);
      setEditModalOpen(false);
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error updating purchase return:', error);
      setError('Failed to update purchase return');
    }
    setLoading(false);
  };

  // Handle Cancel Edit - Check for unsaved changes
  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setCancelConfirmOpen(true);
    } else {
      setEditModalOpen(false);
      setHasUnsavedChanges(false);
      setOriginalEditData(null);
      setOriginalEditDetails([]);
    }
  };

  // Confirm cancel without saving
  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setEditModalOpen(false);
    setHasUnsavedChanges(false);
    setOriginalEditData(null);
    setOriginalEditDetails([]);
  };

  // Close cancel confirmation and stay in edit mode
  const handleCloseCancelConfirm = () => {
    setCancelConfirmOpen(false);
  };

  // Print Purchase Return handler
  const handlePrint = async (purchaseReturnId) => {
    setPrintingItemId(purchaseReturnId);
    try {
      // Fetch purchase return details
      const response = await fetchData('PurchaseReturnApi', `/${purchaseReturnId}`, token);
      const data = response.response_data || response;

      if (!data) {
        setError('Failed to fetch purchase return details.');
        setPrintingItemId(null);
        return;
      }

      const returnData = data.PurchaseReturn || data;
      const details = data.PurchaseReturnDetails || [];

      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode === '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName,
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber,
        phone: branchData.ContactNumber1 + (branchData.ContactNumber2 ? ',' + branchData.ContactNumber2 : '') + (branchData.ContactNumber3 ? ',' + branchData.ContactNumber3 : ''),
        email: branchData.Email,
        vatNumber: branchData.VATNumber
      };

      // Fetch supplier/vendor address details
      let supplierAddress = '';
      let supplierAddress2 = '';
      let supplierVATNumber = returnData.GSTNumber || returnData.VATNumber || '';
      if (returnData.MasterId) {
        try {
          const vendorResponse = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${returnData.MasterId}`, token);
          const vendorData = vendorResponse.response_data?.Ledger || vendorResponse.response_data || vendorResponse || {};
          supplierAddress = vendorData.AddressLine1 || '';
          supplierAddress2 = vendorData.AddressLine2 || '';
          if (!supplierVATNumber) supplierVATNumber = vendorData.VATNumber || '';
        } catch (err) {
          console.error('Error fetching vendor details:', err);
        }
      }

      const purchaseReturnDataForPDF = {
        ...returnData,
        purchaseReturnDetails: details,
        SupplierName: returnData.MasterName || '',
        SupplierAddress: supplierAddress,
        SupplierAddress2: supplierAddress2,
        VATNumber: supplierVATNumber,
        BillPrefix: returnData.BillPrefix || '',
        FinYear: returnData.FinYear || '',
        BillNumber: returnData.BillNumber,
        BillDate: returnData.BillDate,
        PurchaseNo: returnData.PurchaseNo || '',
        PurchaseDate: returnData.PurchaseDate || '',
        InvoiceTypeName: returnData.InvoiceTypeName || '',
        CurrencyName: returnData.CurrencyName || '',
        TotalAmount: returnData.TotalAmount || 0,
        TaxableAmount: returnData.TaxableAmount || 0,
        VATAmount: returnData.VATAmount || 0,
        FreightAmount: returnData.FreightAmount || 0,
        FreightVATAmount: returnData.FreightVATAmount || 0,
        PackingAmount: returnData.PackingAmount || 0,
        PackingVATAmount: returnData.PackingVATAmount || 0,
        TCSAmount: returnData.TCSAmount || 0,
        RoundOff: returnData.RoundOff || 0,
        PaidAmount: returnData.PaidAmount || 0,
        BalanceAmount: returnData.BalanceAmount || 0,
        Remarks: returnData.Remarks || ''
      };

      await printPurchaseReturnPDF(purchaseReturnDataForPDF, companyDetails);
      setSuccess('Print dialog opened!');
    } catch (error) {
      console.error('Error printing:', error);
      setError('Failed to print debit note.');
    } finally {
      setPrintingItemId(null);
    }
  };

  // Handle Delete - Open confirmation modal
  const handleDelete = (purchaseReturn) => {
    setDeleteItemId(purchaseReturn.Id);
    setDeleteItemInfo({
      billNumber: purchaseReturn.BillNumber,
      supplier: purchaseReturn.MasterName
    });
    setDeleteConfirmOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deleteItemId) return;

    setDeleteConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Deleting purchase return...');
    try {
      await deleteData('PurchaseReturnApi', deleteItemId, token);
      setSuccess('Purchase return deleted successfully');
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error deleting purchase return:', error);
      setError('Failed to delete purchase return');
    }
    setLoading(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', supplier: '' });
  };

  // Close delete confirmation
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', supplier: '' });
  };

  // Handle Activate - Open confirmation modal
  const handleActivate = (purchaseReturn) => {
    setActivateItemId(purchaseReturn.Id);
    setActivateItemInfo({
      billNumber: purchaseReturn.BillNumber,
      supplier: purchaseReturn.MasterName
    });
    setActivateConfirmOpen(true);
  };

  // Confirm Activate
  const handleConfirmActivate = async () => {
    if (!activateItemId) return;

    setActivateConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Activating purchase return...');
    try {
      // Use PUT request to activate endpoint
      // await updateData('PurchaseReturnApi', `/${activateItemId}/activate`, {}, token);
      await activateData('PurchaseReturnApi', `${activateItemId}`, token);
      setSuccess('Purchase return activated successfully');
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error activating purchase return:', error);
      setError('Failed to activate purchase return');
    }
    setLoading(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', supplier: '' });
  };

  // Close activate confirmation
  const handleCloseActivateConfirm = () => {
    setActivateConfirmOpen(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', supplier: '' });
  };

  // Handle Open Create Modal
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Handle Close Create Modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    window.location.reload();
  };

  // Handle Save Purchase Return
  const handleSavePurchaseReturn = async (formData) => {
    console.log('Saving purchase return:', formData);

    // Validation
    const validationErrors = [];

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = formData.items.filter(item => (item.rcvdQty || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const vatCalculationType = formData.vatType === 'Including Tax' ? 2 : 1;

      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseNo: formData.purchaseNo || '',
          PurchaseDate: formData.purchaseDate || null,
          BillNumber: formData.billNo,
          BillDate: formData.billDate,
          MasterId: parseInt(formData.supplierId),
          MasterName: formData.supplier,
          InvoiceTypeId: 0,
          VATNumber: formData.gstNo || '',
          VATCalculationType: vatCalculationType,
          VATCalculationTypeName: formData.vatType || 'Excluding Tax',
          Amount: parseFloat(formData.summary.subtotal) || 0,
          DiscountAmount: parseFloat(formData.summary.discount) || 0,
          TaxableAmount: parseFloat(formData.summary.taxable) || 0,
          //CGSTAmount: parseFloat(formData.summary.cgst) || 0,
          VATAmount: parseFloat(formData.summary.vatAmount) || 0,
          IGSTAmount: parseFloat(formData.summary.igst) || 0,
          FreightAmount: parseFloat(formData.additionalCharges.freight) || 0,
          FreightVATRate: parseFloat(formData.summary.effectiveVATRate) || 0,
          FreightVATAmount: parseFloat(formData.summary.freightVAT) || 0,
          PackingAmount: parseFloat(formData.additionalCharges.packing) || 0,
          PackingVATRate: parseFloat(formData.summary.effectiveVATRate) || 0,
          PackingVATAmount: parseFloat(formData.summary.packingVAT) || 0,
          TCSAmount: parseFloat(formData.additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(formData.summary.roundOff) || 0,
          TotalAmount: parseFloat(formData.summary.total) || 0,
          PaidAmount: parseFloat(formData.additionalCharges.paidAmount) || 0,
          BalanceAmount: parseFloat(formData.summary.total - formData.additionalCharges.paidAmount) || 0,
          Remarks: formData.additionalCharges.remarks || '',
          IsActive: true
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(item => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseQuantity: parseFloat(item.purchasedQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          VATRate: parseFloat(item.vatPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          //CGSTAmount: parseFloat(item.cgst) || 0,
          VATAmount: parseFloat(item.vatAmount) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true
        }))
      };

      console.log('Data to send:', dataToSend);

      await postData('PurchaseReturnApi', '', dataToSend, token);

      setSuccess('Purchase return created successfully!');
      setCreateModalOpen(false);
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  // Format currency
  const formatCurrency = (amount, currencyName) => {
    const name = currencyName || '';
    const getSymbol = (n) => {
      if (!n) return 'د.إ';
      const lower = n.toLowerCase().trim();
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'bdt': '৳', 'taka': '৳',
        'pkr': '₨', 'pakistani rupee': '₨',
        'omr': 'OMR', 'omani rial': 'OMR',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD'
      };
      if (symbolMap[lower]) return symbolMap[lower];
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (lower.includes(key)) return symbol;
      }
      return n || 'د.إ';
    };
    const symbol = getSymbol(name);
    return `${symbol} ${(parseFloat(amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  // Filter purchase returns
  const filteredReturns = Array.isArray(purchaseReturns) ? purchaseReturns.filter(pr => {
    // Search filter
    if (searchTerm && !pr.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.PurchaseNo?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    // Date range filter
    if (fromDate && new Date(pr.BillDate) < new Date(fromDate)) return false;
    if (toDate && new Date(pr.BillDate) > new Date(toDate)) return false;

    // Payment Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'paid' && (pr.BalanceAmount > 0 || pr.TotalAmount !== pr.PaidAmount)) return false;
      if (filterStatus === 'unpaid' && (pr.PaidAmount > 0 || pr.BalanceAmount === 0)) return false;
      if (filterStatus === 'partial' && (pr.PaidAmount === 0 || pr.BalanceAmount === 0)) return false;
    }

    // Active/Inactive filter
    if (filterActive !== 'all') {
      if (filterActive === 'active' && pr.IsActive !== true) return false;
      if (filterActive === 'inactive' && pr.IsActive !== false) return false;
    }

    return true;
  }) : [];

  // Pagination
  const totalPages = Math.ceil(filteredReturns.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredReturns.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay open={loading} message={loadingMessage} />}

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Assignment sx={{ fontSize: 24, color: '#0f3460' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Purchase Return Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
        <div style={{
          marginBottom: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            minWidth: 'fit-content',
            paddingBottom: '4px'
          }}>
            {/* Filter Icon - Mobile Only */}
            {isMobile && (
              <Tooltip title="Filters">
                <IconButton
                  size="small"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #0f3460',
                    color: '#0f3460',
                    backgroundColor: showMobileFilters ? '#f0f4ff' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                    },
                  }}
                >
                  <FilterList sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '100px' : '120px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  width: isMobile ? '115px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '100px' : '120px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  width: isMobile ? '115px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* Desktop Filters - Hidden on Mobile */}
            {!isMobile && (
              <>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search by Bill No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  sx={{
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                />

                {/* Status Filter */}
                <TextField
                  select
                  size="small"
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  sx={{
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </TextField>

                {/* Active Filter */}
                <TextField
                  select
                  size="small"
                  value={filterActive}
                  onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }}
                  sx={{
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </>
            )}

            {/* Refresh Button */}
            {isMobile ? (
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={fetchPurchaseReturns}
                  disabled={loading}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #0f3460',
                    color: '#0f3460',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={fetchPurchaseReturns}
                disabled={loading}
                startIcon={<Refresh />}
                sx={{
                  height: '32px',
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#0f3460',
                  color: '#0f3460',
                  '&:hover': {
                    borderColor: '#5568d3',
                    backgroundColor: '#f0f4ff',
                  },
                }}
              >
                Refresh
              </Button>
            )}

            {/* Add Button */}
            {canCreateTransaction && (isMobile ? (
              <Tooltip title="Add Return">
                <IconButton
                  size="small"
                  onClick={() => handleOpenCreateModal()}
                  disabled={loading}
                  sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: '#0f3460',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#5568d3',
                    },
                  }}
                >
                  <Add sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => handleOpenCreateModal()}
                disabled={loading}
                sx={{
                  height: '32px',
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#0f3460',
                  '&:hover': {
                    backgroundColor: '#5568d3',
                  },
                }}
              >
                Add Return
              </Button>
            ))}
          </div>
        </div>

        {/* Mobile Filters Collapse */}
        {isMobile && showMobileFilters && (
          <Box sx={{
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by Bill No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={filterActive}
                  onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Table Section */}
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', height: '27px', textTransform: 'uppercase' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Sl No.</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Return No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Purchase No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Total Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', height: '27px' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'white', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                    No purchase returns found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((pr, index) => (
                  <TableRow
                    key={pr.Id}
                    sx={{
                      backgroundColor: pr.IsActive ? 'transparent' : '#fee2e2',
                      '&:hover': { backgroundColor: pr.IsActive ? '#f9fafb' : '#fecaca' },
                      borderBottom: '1px solid #e5e7eb',
                      height: '27px'
                    }}
                  >
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#6b7280', fontWeight: 500, height: '27px' }}>
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Assignment sx={{ fontSize: 18, color: '#1e40af' }} />
                        <span style={{ fontWeight: 600, color: '#1e40af' }}>
                          {pr.BillNumber}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', height: '27px' }}>
                      {formatDate(pr.BillDate)}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', fontWeight: 500, height: '27px' }}>
                      {pr.PurchaseNo || '-'}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', color: '#374151', height: '27px' }}>
                      {pr.MasterName}
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <Chip
                        label={formatCurrency(pr.TotalAmount, pr.CurrencyName)}
                        size="small"
                        sx={{
                          bgcolor: '#d1fae5',
                          color: '#065f46',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', fontSize: '11px', height: '27px' }}>
                      <Chip
                        label={pr.IsActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: pr.IsActive ? '#d1fae5' : '#fee2e2',
                          color: pr.IsActive ? '#065f46' : '#991b1b',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '4px 12px', textAlign: 'right', height: '27px' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        {pr.IsActive ? (
                          <>
                            <Tooltip title="View">
                              <Button
                                size="small"
                                onClick={() => handleView(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Print">
                              <Button
                                size="small"
                                onClick={() => handlePrint(pr.Id)}
                                disabled={printingItemId === pr.Id}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                {printingItemId === pr.Id ? '⏳' : '🖨️'}
                              </Button>
                            </Tooltip>
                            {canEditTransaction && (
                              <Tooltip title="Edit">
                                <Button
                                  size="small"
                                  onClick={() => handleEdit(pr)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✏️
                                </Button>
                              </Tooltip>
                            )}
                            {canDeleteTransaction && (
                              <Tooltip title="Delete">
                                <Button
                                  size="small"
                                  onClick={() => handleDelete(pr)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  🗑️
                                </Button>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <>
                            <Tooltip title="View">
                              <Button
                                size="small"
                                onClick={() => handleView(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Activate">
                              <Button
                                size="small"
                                onClick={() => handleActivate(pr)}
                                style={{
                                  minWidth: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  color: 'white',
                                  padding: '2px',
                                  boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                  border: 'none',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✅
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredReturns.length > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            marginTop: '12px',
            padding: '8px 0',
            gap: { xs: '12px', sm: '0' }
          }}>
            <Typography variant="body2" sx={{ fontSize: '11px', color: '#6b7280', textAlign: { xs: 'center', sm: 'left' } }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredReturns.length)} of {filteredReturns.length} entries
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: '8px',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '11px', color: '#6b7280' }}>Rows per page:</Typography>
                <TextField
                  select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  sx={{
                    width: '70px',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '11px',
                      height: '28px',
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  First
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Prev
                </Button>
                {Array.from({ length: totalPages }, (_, i) => {
                  const page = i + 1;
                  // Show fewer page numbers on mobile
                  const isMobile = window.innerWidth < 600;
                  if (isMobile) {
                    if (page === currentPage || page === 1 || page === totalPages) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: currentPage === page ? '#5568d3' : '#f9fafb'
                            }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 1 || page === currentPage + 1) {
                      return <span key={page} style={{ padding: '0 2px', color: '#9ca3af' }}>...</span>;
                    }
                    return null;
                  } else {
                    // Desktop view - show more pages
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <Button
                          key={page}
                          size="small"
                          onClick={() => handlePageChange(page)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: currentPage === page ? '#0f3460' : 'white',
                            color: currentPage === page ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: currentPage === page ? '#5568d3' : '#f9fafb'
                            }
                          }}
                        >
                          {page}
                        </Button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>;
                    }
                    return null;
                  }
                })}
                <Button
                  size="small"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Next
                </Button>
                <Button
                  size="small"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  sx={{
                    minWidth: { xs: '50px', sm: '60px' },
                    height: '28px',
                    fontSize: '11px',
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    color: '#374151',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    '&:disabled': { color: '#d1d5db' }
                  }}
                >
                  Last
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fafbfc',
          p: 1.25,
          pb: 1
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.875rem' }}>
                Edit Purchase Return
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                Modify return details and items
              </Typography>
            </Box>
            <IconButton
              onClick={handleCancelEdit}
              size="small"
              sx={{
                bgcolor: '#f1f5f9',
                '&:hover': { bgcolor: '#e2e8f0' },
                p: 0.5
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#fafbfc' }}>
          {selectedReturn && (
            <>
              {/* Header Form - Yellow Background */}
              <Box sx={{
                bgcolor: '#fef3c7',
                borderRadius: 1.5,
                p: 1,
                mb: 1.5,
                border: '1px solid #fde68a',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4} md={1.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      R.NO *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={editFormData.BillNumber || ''}
                      disabled
                      sx={{
                        bgcolor: '#f9fafb',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          height: '28px'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '6px 8px'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={1.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      R.DATE *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={editFormData.BillDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, BillDate: e.target.value })}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          height: '28px'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '6px 8px'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      SUPPLIER *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={editFormData.MasterName || ''}
                      disabled
                      sx={{
                        bgcolor: '#f9fafb',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          height: '28px'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '6px 8px'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      VAT TYPE *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      value={vatType}
                      onChange={(e) => handleVATTypeChange(e.target.value)}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          height: '28px'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '6px 8px'
                        }
                      }}
                    >
                      <MenuItem value={1}>Excluding Tax</MenuItem>
                      <MenuItem value={2}>Including Tax</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      CURRENCY
                    </Typography>
                    <Autocomplete
                      options={Array.isArray(editCurrencies) ? editCurrencies : []}
                      getOptionLabel={(option) => option.Currency || ''}
                      isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                      value={Array.isArray(editCurrencies) && editFormData.CurrencyTypeId ? editCurrencies.find(c => c.Id === parseInt(editFormData.CurrencyTypeId)) || null : null}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setEditFormData(prev => ({
                            ...prev,
                            CurrencyTypeId: newValue.Id,
                            CurrencyName: newValue.Currency || ''
                          }));
                        } else {
                          setEditFormData(prev => ({
                            ...prev,
                            CurrencyTypeId: null,
                            CurrencyName: ''
                          }));
                        }
                      }}
                      disabled={editCurrencyLoading}
                      loading={editCurrencyLoading}
                      ListboxProps={{
                        style: { fontSize: '0.7rem', maxHeight: '200px' }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Select Currency"
                          sx={{
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              fontSize: '0.7rem',
                              height: '28px'
                            }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: '0.7rem' },
                            endAdornment: (
                              <>
                                {editCurrencyLoading ? <CircularProgress color="inherit" size={14} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                        '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.7rem' },
                        '& .MuiAutocomplete-input': { py: '3.5px !important' },
                        '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                        '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      REMARKS
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter return remarks..."
                      value={editFormData.Remarks || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, Remarks: e.target.value })}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          height: '28px'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 10px'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.75rem' }}>
                  Return Items
                </Typography>
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ bgcolor: '#fef3c7', borderBottom: '2px solid #fde68a' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>ITEM</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>HSN</th>
                          {editFormData.PurchaseNo && (
                            <>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>PURCHASED</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>BALANCE QTY</th>
                            </>
                          )}
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>RETURN QTY *</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>RATE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>AMOUNT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC AMT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT%</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>TAXABLE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT AMOUNT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.6rem', textTransform: 'uppercase' }}>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editReturnDetails.map((item, index) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                {index + 1}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.7rem' }}>
                                {item.ItemName}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.HSNCode || '-'}
                              </Typography>
                            </td>
                            {editFormData.PurchaseNo && (
                              <>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    {item.PurchasedQuantity}
                                  </Typography>
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    {item.BalanceQuantity}
                                  </Typography>
                                </td>
                              </>
                            )}
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={item.ReturnQuantity ?? ''}
                                onChange={(e) => handleReturnQuantityChange(index, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  max: item.BalanceQuantity,
                                  step: 1,
                                  style: { textAlign: 'center', fontSize: '0.813rem' }
                                }}
                                sx={{
                                  width: '70px',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    padding: '6px 8px'
                                  }
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.813rem' }}>
                                {formatCurrency(item.Rate, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.813rem' }}>
                                {formatCurrency(item.Amount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={item.DiscountPercentage ?? ''}
                                onChange={(e) => handleDiscountPercentChange(index, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  max: 100,
                                  step: 0.01,
                                  style: { textAlign: 'right', fontSize: '0.813rem' }
                                }}
                                sx={{
                                  width: '70px',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    padding: '6px 8px'
                                  }
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={item.DiscountAmount ?? ''}
                                onChange={(e) => handleDiscountAmountChange(index, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  step: 0.01,
                                  style: { textAlign: 'right', fontSize: '0.813rem' }
                                }}
                                sx={{
                                  width: '80px',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    padding: '6px 8px'
                                  }
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600, fontSize: '0.813rem' }}>
                                {item.VATRate || 0}%
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.813rem' }}>
                                {formatCurrency(item.TaxableAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.813rem' }}>
                                {formatCurrency((item.VATAmount || 0), editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.813rem' }}>
                                {formatCurrency(item.TotalAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Return Summary Section */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, fontSize: '0.938rem' }}>
                  Return Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  {/* Additional Charges */}
                  <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    p: 1.5,
                    border: '1px solid #e2e8f0',
                    flex: '0 0 auto',
                    width: { xs: '100%', md: '400px' }
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, fontSize: '0.875rem' }}>
                      Additional Charges
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.3, display: 'block', textTransform: 'uppercase', fontSize: '0.688rem' }}>
                          FREIGHT AMOUNT
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.688rem', mb: 0.5, display: 'block' }}>
                          VAT Rate: {editFormData.FreightVATRate || 5}%
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editFormData.FreightAmount || 0}
                          onChange={(e) => handleAdditionalChargeChange('FreightAmount', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.813rem' } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '6px 8px'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.3, display: 'block', textTransform: 'uppercase', fontSize: '0.688rem' }}>
                          PACKING AMOUNT
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.688rem', mb: 0.5, display: 'block' }}>
                          VAT Rate: {editFormData.PackingVATRate || 5}%
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editFormData.PackingAmount || 0}
                          onChange={(e) => handleAdditionalChargeChange('PackingAmount', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.813rem' } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '6px 8px'
                            }
                          }}
                        />
                      </Grid>
                      {/* <Grid item xs={6}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block', textTransform: 'uppercase', fontSize: '0.688rem' }}>
                          TCS AMOUNT
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editFormData.TCSAmount || 0}
                          onChange={(e) => handleAdditionalChargeChange('TCSAmount', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.813rem' } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '6px 8px'
                            }
                          }}
                        />
                      </Grid> */}
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block', textTransform: 'uppercase', fontSize: '0.688rem' }}>
                          PAID AMOUNT
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.688rem', mb: 0.5, display: 'block' }}>
                          .
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editFormData.PaidAmount || 0}
                          onChange={(e) => setEditFormData({ ...editFormData, PaidAmount: parseFloat(e.target.value) || 0 })}
                          onFocus={(e) => e.target.select()}
                          inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.813rem' } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '6px 8px'
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Return Calculation */}
                  <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    p: 1,
                    border: '1px solid #e2e8f0',
                    flex: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.8, fontSize: '0.75rem' }}>
                      Return Calculation
                    </Typography>
                    <Grid container spacing={0.8}>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Subtotal
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.Amount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Discount
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.DiscountAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Taxable
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.TaxableAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            VAT Total
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency((editFormData.VATAmount || 0), editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Freight
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.FreightAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={0.8} sx={{ mt: 0.5 }}>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Freight VAT
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.FreightVATAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Packing
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.PackingAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Packing VAT
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.7rem' }}>
                            {formatCurrency(editFormData.PackingVATAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            TCS
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.813rem' }}>
                            {formatCurrency(editFormData.TCSAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid> */}
                      <Grid item xs={2.4}>
                        <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 600 }}>
                            Round Off
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.7rem' }}>
                            {editFormData.RoundOff >= 0 ? '+ ' : '- '}{formatCurrency(Math.abs(editFormData.RoundOff || 0), editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={0.8} sx={{ mt: 0.5 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fef3c7', borderRadius: 1, border: '1.5px solid #fde68a' }}>
                          <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.625rem', fontWeight: 700 }}>
                            Return Total Amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#ea580c', mt: 0.2, fontSize: '1rem' }}>
                            {formatCurrency(editFormData.TotalAmount || 0, editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#dbeafe', borderRadius: 1, border: '1.5px solid #93c5fd' }}>
                          <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.625rem', fontWeight: 700 }}>
                            Balance Amount
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.2, fontSize: '1rem' }}>
                            {formatCurrency((editFormData.TotalAmount || 0) - (editFormData.PaidAmount || 0), editFormData.CurrencyName || selectedReturn?.CurrencyName)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>

            </>
          )}
        </DialogContent>
        <Box sx={{
          p: 1.5,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCancelEdit}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669, #047857)'
              }
            }}
          >
            Update
          </Button>
        </Box>
      </Dialog>

      {/* View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#fafbfc',
          p: 2,
          pb: 1.5
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#0f172a', fontSize: '0.875rem' }}>
                  View Purchase Return
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                  Purchase return details and items
                </Typography>
              </Box>
              {selectedReturn?.IsActive === false && (
                <Chip
                  label="INACTIVE"
                  size="small"
                  sx={{
                    bgcolor: '#991b1b',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '20px',
                    letterSpacing: '0.5px'
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={() => setViewModalOpen(false)}
              size="small"
              sx={{
                bgcolor: selectedReturn?.IsActive === false ? '#fecaca' : '#f1f5f9',
                '&:hover': { bgcolor: selectedReturn?.IsActive === false ? '#fca5a5' : '#e2e8f0' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: selectedReturn?.IsActive === false ? '#fef2f2' : '#fafbfc' }}>
          {selectedReturn && (
            <>
              {/* Header Information - Conditional Background */}
              <Box sx={{
                bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe',
                borderRadius: 1.5,
                p: 1,
                mb: 1.5,
                border: selectedReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                      {selectedReturn.BillNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {formatDate(selectedReturn.BillDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Purchase No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedReturn.PurchaseNo || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Supplier
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedReturn.MasterName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      VAT Type
                    </Typography>
                    <Chip
                      label={selectedReturn.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax'}
                      size="small"
                      sx={{
                        bgcolor: selectedReturn.VATCalculationType === 2 ? '#dcfce7' : '#dbeafe',
                        color: selectedReturn.VATCalculationType === 2 ? '#166534' : '#1e40af',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    {/* <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Invoice Type
                    </Typography>
                    <Chip
                      label={selectedReturn.InvoiceTypeName || 'N/A'}
                      size="small"
                      sx={{
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    /> */}
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Currency
                    </Typography>
                    <Chip
                      label={selectedReturn.CurrencyName || 'N/A'}
                      size="small"
                      sx={{
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  {selectedReturn.Remarks && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Remarks
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontStyle: 'italic' }}>
                        {selectedReturn.Remarks}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Items
                </Typography>
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderBottom: selectedReturn?.IsActive === false ? '2px solid #fca5a5' : '2px solid #93c5fd' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>ITEM NAME</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>HSN CODE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>QUANTITY</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>RATE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>AMOUNT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC AMT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TAXABLE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT Amount</th>
                          {/* <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>IGST</th> */}
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnDetails.map((item, index) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                {index + 1}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.7rem' }}>
                                {item.ItemName}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.HSNCode || '-'}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={item.Quantity}
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {formatCurrency(item.Rate, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.Amount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.DiscountPercentage || 0}%
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.DiscountAmount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={`${item.VATRate || 0}%`}
                                size="small"
                                sx={{
                                  bgcolor: '#d1fae5',
                                  color: '#065f46',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TaxableAmount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.VATAmount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                            {/* <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.IGSTAmount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td> */}
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TotalAmount || 0, selectedReturn?.CurrencyName)}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Return Summary Section */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Summary
                </Typography>
                <Grid container spacing={1.5}>
                  {/* Financial Summary */}
                  <Grid item xs={12} md={8}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Subtotal
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.Amount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Discount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.DiscountAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Taxable Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.TaxableAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              VAT Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.VATAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        {/* <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              IGST
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.IGSTAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid> */}
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.FreightAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.FreightVATAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.PackingAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.PackingVATAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                        {/* <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              TCS Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedReturn.TCSAmount || 0, selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid> */}
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Round Off
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {selectedReturn.RoundOff >= 0 ? '+' : ''} {formatCurrency(Math.abs(selectedReturn.RoundOff || 0), selectedReturn?.CurrencyName)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Total Summary */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dbeafe', borderRadius: 1, border: '2px solid #93c5fd' }}>
                        <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Total Return Amount
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.3, fontSize: '1.25rem' }}>
                          {formatCurrency(selectedReturn.TotalAmount || 0, selectedReturn?.CurrencyName)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fef3c7', borderRadius: 1, border: '2px solid #fde68a' }}>
                        <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Paid Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ea580c', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedReturn.PaidAmount || 0, selectedReturn?.CurrencyName)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dcfce7', borderRadius: 1, border: '2px solid #86efac' }}>
                        <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Balance Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedReturn.BalanceAmount || ((selectedReturn.TotalAmount || 0) - (selectedReturn.PaidAmount || 0)), selectedReturn?.CurrencyName)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{
          p: 1,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            variant="outlined"
            onClick={() => setViewModalOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1,
              px: 2.5,
              py: 0.5,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                bgcolor: '#eff6ff'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCloseCancelConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef3c7',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e', fontSize: '1.125rem' }}>
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6 }}>
            You have unsaved changes. Are you sure you want to cancel without saving? All changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseCancelConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Keep Editing
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancel}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef2f2',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#ef4444', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b', fontSize: '1.125rem' }}>
            Delete Purchase Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to delete this purchase return?
          </Typography>
          {deleteItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {deleteItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Supplier:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {deleteItemInfo.supplier}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog
        open={activateConfirmOpen}
        onClose={handleCloseActivateConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#d1fae5',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <CheckCircle sx={{ color: '#10b981', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46', fontSize: '1.125rem' }}>
            Activate Purchase Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to activate this purchase return?
          </Typography>
          {activateItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {activateItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Supplier:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {activateItemInfo.supplier}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#10b981', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ✓ This will restore the purchase return to active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseActivateConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmActivate}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669, #047857)'
              }
            }}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Purchase Return Modal */}
      <PurchaseReturnModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSavePurchaseReturn}
        companyId={companyId}
        branchId={branchId}
        token={token}
        suppliers={suppliers}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
        setError={setError}
        setSuccess={setSuccess}
        fetchPurchaseReturns={fetchPurchaseReturns}
        setCreateModalOpen={setCreateModalOpen}
        userId={userId}
      />
    </div>
  );
};

export default PurchaseReturnAgainstIndex;
